/* eslint-disable no-param-reassign */
/* eslint-disable no-async-promise-executor */
import React, {
	Context,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	batch,
	useDispatch,
	useSelector,
} from 'react-redux';
import {
	Publisher,
	Stream,
	Subscriber,
} from '@opentok/client';
import {
	setRestrictedUserState,
	setScreenShareWebrtcId,
	setWebrtcId,
	userUpdated,
} from '../../stores/actions/session';
import {
	addPresenterVideo,
	removePresenterVideo,
	setConnectedPresenterVideos,
	setSharingScreen,
	updateLocalId,
	addFeedWebRTCId,
	removeFeedWebRTCId,
	clearFeedWebRTCIds,
} from '../../stores/actions/video';
import {
	getUserIdFromMobileStream,
	isLocalUser,
	streamIsMobile,
	streamIsScreenshare,
} from '../../utils/utils';
import { GetTokboxToken, SetStreamInfo } from '../../connection/opentok';
import { OPENTOK_STREAM_AUDIO_REMOVE_UNMUTED_ID, setOpenTokStats } from '../../stores/actions/opentok';
import { GreenroomState as GRS } from '../../stores/types/types';
import OpenTok, { TotalOTStats, VideoQuality } from '../../utils/video/opentok-connection';
import { AVContext, AVDataInterface } from '../audio-video';
import { VideoStream } from '../../components/types/videos';
import Socket from '../../connection/socket';
import { RESTRICTED_CONNECTION_TYPES, RESTRICTED_USER_STATES } from '../../stores/reducers/session';
import { ReportError } from '../../connection/errors';
import { Shot } from '../../stores/reducers/shots';
import { showAlert } from '../../components/general-components/alert-service';
import { ROOM_STATES } from '../../stores/reducers/settings';

import usePrevious from '../../utils/use-previous';
import store from '../../stores/main';
import { CONNECTED_PRESENTER_VIDEO_TYPES } from '../../types/constants';
import * as VideoActions from '../../stores/actions/video';

export interface OpenTokContextInterface {
	opentok: OpenTok | null;
	forceFeedsTabRefresh: boolean;
	setOT: Function;
	initialSetup: Function;
	restartPublisher: Function;
}

interface BackstageCommsSocketMessage {
	type: string;
	message: {
		muted: boolean;
		producerID: string;
		targetedPresenterID: string;
	};
}

export const OpenTokContext = React.createContext(
	{
		opentok: null,
		forceFeedsTabRefresh: false,
		setOT: () => {},
		initialSetup: () => {},
		restartPublisher: () => {},
	},
) as unknown as Context<OpenTokContextInterface>;

// what you pass into the React.provider component as the value
export function useOpentok(socket: Socket | null): OpenTokContextInterface {
	const dispatch = useDispatch();

	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const embed = useSelector((s: GRS) => s.SessionReducer.embed);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const watcherOnly = useSelector((s: GRS) => s.SessionReducer.watcher_only);
	const webrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const isOnsiteFeed = useSelector((s: GRS) => s.SessionReducer.isOnsiteFeed);
	const isProgramFeed = useSelector((s: GRS) => s.SessionReducer.isProgramFeed);
	const onsiteUsers = useSelector((s: GRS) => s.SessionReducer.onsiteUsers);
	const expectIosStream = useSelector((s: GRS) => s.SessionReducer.expectIosStream);
	const restrictedUserState = useSelector((s: GRS) => s.SessionReducer.restrictedUserState);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const screenShareWebrtcId = useSelector((s: GRS) => s.SessionReducer.screenShareWebrtcId);
	const selectedVideo = useSelector((s: GRS) => s.VideoReducer.selectedVideo);
	const selectedAudio = useSelector((s: GRS) => s.VideoReducer.selectedAudio);
	const selectedOutput = useSelector((s: GRS) => s.VideoReducer.selectedOutput);
	const sharingScreen = useSelector((s: GRS) => s.VideoReducer.sharingScreen);
	const mainVideoStream = useSelector((s: GRS) => s.VideoReducer.mainVideoStream);
	const connectedMediaVideos = useSelector((s: GRS) => s.VideoReducer.connectedMediaVideos);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const shouldRepublishVideo = useSelector((s: GRS) => s.VideoReducer.shouldRepublishVideo);
	const clearShot = useSelector((s: GRS) => s.SettingsReducer.clearShot);
	const roomState = useSelector((s: GRS) => s.MeetingReducer.roomState);
	const backstageCommsActive = useSelector((s: GRS) => s.SettingsReducer.backstageCommsActive);
	const shots = useSelector((s: GRS) => s.ShotsReducer.shots);
	const currentShotId = useSelector((s: GRS) => s.ShotsReducer.currentShotId);
	const backstageCanvasVolume = useSelector((s: GRS) => s.MeetingReducer.backstageCanvasVolume);
	const unmutedAudioFeeds = useSelector((s: GRS) => s.OpenTokReducer.unmutedAudioFeeds);
	const debugOpentok = useSelector((s: GRS) => s.DebugReducer.debugOpentok);
	const isoArchiveId = useSelector((s: GRS) => s.BroadcastReducer.isoArchiveId);
	const openCommValues = useSelector((s: GRS) => s.CommToggleReducer.entityValue);

	const { setAV, removeAV, getAV } = useContext(AVContext);

	const opentok = useRef<OpenTok | null>(null);
	const opentokElements = useRef<HTMLVideoElement[]>([]);
	const opentokId = useRef<string>('');
	const publishAttempts = useRef<number>(0);
	const startedUp = useRef<boolean>(false);
	const mapOfOpenTokStreamsAttemptedReconnects = useRef<Map<string, number> >(new Map());

	const [forceFeedsTabRefresh, setForceFeedsTabRefresh] = useState<boolean>(false);
	const [opentokInitialized, setOpentokInitialized] = useState<boolean>(false);

	const feedQuality: VideoQuality = { width: 1920, height: 1080, frameRate: 30 };

	const currentShot = useMemo(
		() => shots.find((shot: Shot) => shot.shot_id === currentShotId) ?? shots[0],
		[currentShotId, shots],
	);

	const {
		shot_data: {
			shotFeeds,
		},
	} = currentShot;

	const canvasWebRTCIds = useMemo((): string[] => {
		const newIdsOfVideosInCanvas: string[] = [];
		const len = shotFeeds.length;

		for (let i = 0; i < len; i += 1) {
			const feed = shotFeeds[i];
			if (feed.media) {
				const connectedVideoForMedia = connectedMediaVideos.find(
					(vid: VideoStream) => vid.mediaId === feed.media,
				);
				if (connectedVideoForMedia) {
					newIdsOfVideosInCanvas.push(connectedVideoForMedia.id);
				}
			}

			if (feed.presenter) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.profile === feed.presenter,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}

			if (feed.screenshare) {
				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.email === feed.screenshare);

				if (connectedUser) {
					const connectedVideoForPresenter = connectedPresenterVideos.find(
						(vid: VideoStream) => vid.id === connectedUser.screenShareWebrtcId,
					);

					if (connectedVideoForPresenter) {
						newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
					}
				}
			}

			if (!feed.presenter && feed.email) {
				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.email === feed.email);

				if (connectedUser) {
					const connectedVideoForPresenter = connectedPresenterVideos.find(
						(vid: VideoStream) => vid.profile === connectedUser.gr_user,
					);

					if (connectedVideoForPresenter) {
						newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
					}
				}
			}
		}

		return newIdsOfVideosInCanvas;
	}, [connectedMediaVideos, connectedPresenterVideos, connectedUsers, shotFeeds]);

	const isOnCanvas = useMemo(
		() => !!((webrtcID && canvasWebRTCIds.includes(webrtcID.toString()))
			|| (screenShareWebrtcId && canvasWebRTCIds.includes(screenShareWebrtcId.toString()))),
		[canvasWebRTCIds, webrtcID],
	);

	const backstageCanvasVolumeOn = useMemo(
		() => !embed && !isOnCanvas && roomState !== ROOM_STATES.EDITING,
		[embed, isOnCanvas, roomState],
	);

	const getLocalMuteState = () => {
		const av = getAV('local');

		return {
			videoMuted: !av.video,
			audioMuted: !av.audio,
		};
	};

	const setOT = (newOpentok: any): void => {
		opentok.current = newOpentok;
	};

	const resetOT = () => {
		opentok.current = null;
		opentokElements.current = [];
		opentokId.current = '';
		publishAttempts.current = 0;
		startedUp.current = false;
		mapOfOpenTokStreamsAttemptedReconnects.current = new Map();

		if (socket?.listeners.has('backstage-comms-toggle')) {
			socket?.removeListener('backstage-comms-toggle');
		}
	};

	const videoFeedError = () => {
		setForceFeedsTabRefresh(true);
		setTimeout(() => setForceFeedsTabRefresh(false), 16);
	};

	const updateWebRTCId = (webRTCId: string | number) => {
		dispatch(setWebrtcId(webRTCId));
		opentokId.current = String(webRTCId) ?? '';
		dispatch(updateLocalId(webRTCId));
	};

	// https://api.support.vonage.com/hc/en-us/articles/10901330247964-How-to-update-subscriber-stream-when-manipulating-video-element
	// Due to the introduction of Adaptive Media Routing in OpenTok 2.24.7,
	// subscriber streams may be updated depending on how many users are connected to the session.
	// The following is a modified version of Vonage's recommended way of handling this for custom UI:
	const checkStreamOnPlayback = ({ target }: Event) => {
		if (!target || !(target instanceof HTMLVideoElement)) return;
		const { VideoReducer } = store.getState() as GRS;
		const currentVideoState = VideoReducer.connectedPresenterVideos.find((v) => v.id === target.id);
		if (currentVideoState && currentVideoState.srcObject !== target.srcObject) {
			const updatedPresenterVideos = VideoReducer.connectedPresenterVideos.map((vid) => {
				if (vid.id === target.id && target.srcObject) {
					vid.srcObject = target.srcObject as MediaStream;
				}
				return vid;
			});
			dispatch(setConnectedPresenterVideos(updatedPresenterVideos));
		}
	};

	const streamSubscribed = (event: any) => {
		// This fires when a user joins the same opentok session we are connected to.
		// Our client will subscribe to the stream that they're publishing through opentok.
		const handleStream = (stream: Stream) => {
			const {
				streamId,
				name: streamName,
			} = stream;

			const {
				VideoReducer,
				SessionReducer,
			} = store.getState() as GRS;

			if (!streamId || !streamName) {
				return;
			}

			const isScreenshare = streamIsScreenshare(event.target);
			const isMobile = streamIsMobile(stream);
			const targetProfile = isMobile
				? Number(getUserIdFromMobileStream(stream))
				: Number(streamName.split(',')[0]);

			if ((targetProfile < 0) || Number.isNaN(targetProfile)) {
				return;
			}

			/*
			* video element updates
			*/
			event.element.id = event.target.streamId;

			// in embed mode, automute anyone who connects (they will be unmuted when put into canvas)
			if (SessionReducer.embed) {
				event.element.volume = 0.0;
			}

			if (VideoReducer.selectedOutput && event.element.srcObject.getAudioTracks()?.[0]) {
				event.element.setSinkId(VideoReducer.selectedOutput?.deviceId);
			}

			event.element.onplay = checkStreamOnPlayback;
			opentokElements.current = [...opentokElements.current, event.element];

			/*
			* reducer updates
			*/
			const tempConnectedVideos = [...VideoReducer.connectedPresenterVideos];
			let isUserJoining = true;

			batch(() => {
				const connectedUser = SessionReducer.connectedUsers
					.find((_connectedUser) => _connectedUser.gr_user === targetProfile
						|| _connectedUser.brandlive_profile === targetProfile);

				if (connectedUser) {
					if (targetProfile === SessionReducer.user?.gr_user) {
						dispatch(addFeedWebRTCId(streamId));
					}

					// sad reverse compatibility things
					if (isScreenshare) {
						connectedUser.screenShareWebrtcId = streamId;

						if (targetProfile === SessionReducer.user?.gr_user) {
							dispatch(setScreenShareWebrtcId(streamId));
						}
					}

					dispatch(userUpdated(connectedUser));
				}

				for (let i = 0; i < tempConnectedVideos.length; i += 1) {
					// if the connecting user already exists in connected presenters, just update
					if (tempConnectedVideos[i].id === streamId) {
						tempConnectedVideos[i].srcObject = event.element.srcObject;
						tempConnectedVideos[i].videoMuted = stream.hasVideo;
						tempConnectedVideos[i].muted = stream.hasAudio;
						tempConnectedVideos[i].mutedPreference = stream.hasAudio;
						tempConnectedVideos[i].profile = isScreenshare ? 0 : targetProfile;
						tempConnectedVideos[i].isMobile = isMobile;
						tempConnectedVideos[i].isScreenShare = isScreenshare;
						isUserJoining = false;
						break;
					}
				}

				if (isUserJoining) {
					// New user joining
					dispatch(addPresenterVideo({
						srcObject: event.element.srcObject,
						id: streamId,
						videoMuted: stream.hasVideo,
						muted: stream.hasAudio,
						mutedPreference: stream.hasAudio,
						profile: isScreenshare ? 0 : targetProfile,
						isMobile,
						isScreenShare: isScreenshare,
					}));
				} else {
					// No new users, just updates
					dispatch(setConnectedPresenterVideos(tempConnectedVideos));
				}
			});
		};

		if (event.target instanceof Subscriber) {
			handleStream(event.target.stream);
		} else if (event.target instanceof Publisher) {
			(event.target as Publisher).once('streamCreated', (streamEvent) => {
				handleStream(streamEvent.stream);
			});
		}
	};

	const handlePublisherDestroyed = async (e: any) => {
		const {
			VideoReducer,
			SessionReducer,
		} = store.getState() as GRS;

		const stillPublishingVideo = VideoReducer.connectedPresenterVideos.find(
			(vid) => vid.id === e.stream.id,
		);

		if (stillPublishingVideo && opentok.current?.publisher) {
			const webRTCId = e.stream.id ?? '';
			const connectedUser = SessionReducer.connectedUsers
				.find((_connectedUser) => _connectedUser.webrtcID === webRTCId);

			if (connectedUser) {
				dispatch(removeFeedWebRTCId(webRTCId));
			}

			dispatch(removePresenterVideo({ id: webRTCId }));
			opentok.current.publisher = null;
		}
	};

	const initTokbox = () => new Promise(async (resolve, reject) => {
		try {
			if (meeting) {
				const tokboxToken = await GetTokboxToken(meeting.opentok_session);

				if (tokboxToken) {
					opentok.current = new OpenTok(
						user,
						meeting.opentok_session,
						tokboxToken.token,
						(e: AVDataInterface) => setAV(e),
						(id: string) => removeAV(id),
						(e: any) => streamSubscribed(e),
						(openTokStats: TotalOTStats) => dispatch(setOpenTokStats(openTokStats)),
						handlePublisherDestroyed,
						debugOpentok,
						embed,
					);
				} else {
					throw new Error('Unable to get Tokbox token');
				}
				resolve(true);
			}
		} catch (e) {
			console.error(e);
			reject(e);
		}
	});

	// see comment for "backstage comms" above
	// this is a temp. stream unmute for the producer to talk to presenters who are in the shot
	// (or a single presenter, if "targetedPresenterID" is specified)
	const applyBackstageComms = (
		incoming: BackstageCommsSocketMessage,
		newIdsOfVideosInCanvas: string[],
		shotIsCleared: boolean,
		myWebRTCId: string,
	) => {
		const { producerID, targetedPresenterID } = incoming.message;
		let { muted } = incoming.message;
		// if muted is false, the passed producerID (the producer that clicked the button)
		// will have their coresponding opentok element in my app unmuted

		// if im not live, I am backstage, producer is always unmuted
		if (!newIdsOfVideosInCanvas.includes(myWebRTCId?.toString() ?? '')) {
			muted = false;
		}

		// if session & producer are both live on canvas, producer always unmuted
		if (
			newIdsOfVideosInCanvas.includes(myWebRTCId?.toString() ?? '')
			&& newIdsOfVideosInCanvas.includes(producerID)
		) {
			muted = false;
		}

		// if clearShot is true, there is no bsc, force unmute
		if (shotIsCleared) {
			muted = false;
		}

		// session received msg & specific producer id should be muted/unmuted,
		// loop to find a link between producer-publisher webrtcid
		const numberOfStreams = opentokElements.current.length;

		let ii = 0;
		for (ii; ii < numberOfStreams; ii += 1) {
			// if this opentok element belongs to producer
			if (opentokElements.current[ii].id === producerID) {
				// if <muted> for everyone or just for me
				if (!targetedPresenterID || targetedPresenterID === myWebRTCId) {
					opentokElements.current[ii].volume = muted ? 0.0 : 1.0;
				}
			}
		}
	};

	const streamAdded = async (event: any) => {
		const {
			SessionReducer,
		} = store.getState() as GRS;

		try {
			if (event?.stream?.streamId) {
				let attemptsForThisStream = mapOfOpenTokStreamsAttemptedReconnects.current.get(
					event.stream.streamId,
				);

				if (attemptsForThisStream) {
					attemptsForThisStream += 1;
				} else {
					attemptsForThisStream = 1;
				}

				mapOfOpenTokStreamsAttemptedReconnects.current.set(
					event.stream.streamId,
					attemptsForThisStream,
				);

				if (attemptsForThisStream < 11) {
					// remove mobile audio if stream is from self since opentok sees
					// them as two different devices and won't cancel the audio
					if (SessionReducer.user
						&& streamIsMobile(event.stream)
						&& isLocalUser(
							SessionReducer.user,
							getUserIdFromMobileStream(event.stream),
						)
					) {
						event.stream.channel = event.stream.channel.filter(
							(channel_: any) => channel_.type !== 'audio',
						);
					}

					// allow reconnection attempts while preventing repeat errors
					if (event.stream && !event.stream?.destroyed) {
						await opentok.current?.subscribeToStream(event.stream);
					} else {
						setTimeout(async () => {
							await streamAdded(event);
						}, 1000 * attemptsForThisStream);
					}
				}
			}
		} catch (e) {
			console.error(e);

			setTimeout(async () => {
				await streamAdded(event);
			}, 1000);
		}
	};

	const streamRemoved = async (event: any) => {
		const {
			VideoReducer,
			SessionReducer,
		} = store.getState() as GRS;

		try {
			const webRTCId = event.stream.id;

			// get this connected video
			const connectedVideo = VideoReducer.connectedPresenterVideos
				.find((vid: VideoStream) => vid.id === webRTCId);

			dispatch(removePresenterVideo({ id: webRTCId }));

			opentokElements.current = opentokElements.current.filter(
				(el: HTMLVideoElement) => !!el.srcObject,
			);

			if (connectedVideo && connectedVideo.isMobile) {
				const connectedUser = SessionReducer.connectedUsers
					.find((_connectedUser) => _connectedUser.webrtcID
						&& _connectedUser.webrtcID === connectedVideo?.id);

				if (connectedUser) {
					connectedUser.in_room = false;
					if (connectedUser.gr_user === SessionReducer.user?.gr_user) {
						dispatch(removeFeedWebRTCId(webRTCId));
					}
					dispatch(userUpdated(connectedUser));
				}
			}

			// if this was a mobile connection and the connection was from this
			// user and this user is not just a watcher, startup the connected video stream
			if (
				SessionReducer.user
				&& connectedVideo?.isMobile
				&& SessionReducer.embed
				&& connectedVideo?.profile === SessionReducer.user.gr_user
				&& SessionReducer.watcher_only
				&& (VideoReducer.selectedVideo || VideoReducer.selectedAudio)
			) {
				const { videoMuted, audioMuted } = getLocalMuteState();
				await opentok.current?.restartStream(
					feedQuality,
					VideoReducer.selectedVideo?.deviceId,
					VideoReducer.selectedAudio?.deviceId,
					videoMuted,
					audioMuted,
					String(SessionReducer.user.gr_user),
				);

				opentok.current?.toggleVideo(connectedVideo.videoMuted || false);
				opentok.current?.toggleAudio(connectedVideo.muted || false);

				if (opentok.current?.publisher?.streamId) {
					updateWebRTCId(opentok.current.publisher.streamId);
				}

				const connectedUser = SessionReducer.connectedUsers
					.find((_connectedUser) => _connectedUser.webrtcID
						&& _connectedUser.webrtcID === connectedVideo?.id);

				if (connectedUser) {
					connectedUser.in_room = false;
					if (connectedUser.gr_user === SessionReducer.user?.gr_user) {
						dispatch(removeFeedWebRTCId(webRTCId));
						if (opentok.current?.publisher?.streamId) {
							dispatch(addFeedWebRTCId(String(opentok.current?.publisher?.streamId)));
						}
					}

					dispatch(userUpdated(connectedUser));
				}
				dispatch(addPresenterVideo({
					...connectedVideo,
					id: opentok.current?.publisher?.streamId,
					isMobile: false,
					srcObject: VideoReducer.mainVideoStream,
				}));
			}
		} catch (e: any) {
			if (SessionReducer.token) {
				ReportError(
					SessionReducer.token,
					window.navigator.userAgent,
					e.stack,
					e.message,
				).catch((err) => {
					console.error(err);
				});
			}
			console.error(e);
		}
	};

	const handleReconnect = () => {
		const {
			VideoReducer,
			SessionReducer,
		} = store.getState() as GRS;

		const userVideo = {
			id: 'local',
			srcObject: VideoReducer.mainVideoStream,
			videoMuted: VideoReducer.videoMuted,
			muted: VideoReducer.audioMuted,
			mutedPreference: VideoReducer.audioMuted,
			userInitiated: VideoReducer.audioMuted,
			self: true,
			local: true,
			profile: SessionReducer.user?.gr_user ?? 1,
			brandliveProfileId: SessionReducer.user?.brandlive_profile,
			isMedia: false,
		};

		opentok.current?.stop();
		opentok.current = null;
		dispatch(setConnectedPresenterVideos([userVideo]));
		dispatch(clearFeedWebRTCIds());
		videoFeedError();
	};

	const handleScreensharingStop = () => {
		const connectedUser = connectedUsers.find((_connectedUser) => _connectedUser.gr_user
			=== user?.gr_user);

		if (connectedUser && connectedUser.screenShareWebrtcId) {
			dispatch(removeFeedWebRTCId(String(connectedUser.screenShareWebrtcId)));
			dispatch(userUpdated(connectedUser));
		}

		dispatch(setSharingScreen(false));
		dispatch(setScreenShareWebrtcId(''));
	};

	const startupOpentokScreenshare = async () => {
		try {
			const connectedUser = connectedUsers
				.find((_connectedUser) => _connectedUser.gr_user === user?.gr_user);
			const canScreenshare = await opentok.current?.checkScreenshare();

			if (!connectedUser || !canScreenshare) {
				return;
			}

			await opentok.current?.startScreensharing(handleScreensharingStop);
		} catch (e: any) {
			console.error(e);

			dispatch(setSharingScreen(false));

			if (token) {
				ReportError(
					token,
					window.navigator.userAgent,
					e.stack,
					e.message,
				).catch((e2) => {
					console.error(e2);
				});
			}

			showAlert({
				message: 'Screen share error',
				description: 'We ran into an issue capturing your screen. Please try again.',
				duration: 5000,
				type: 'neutral',
			});
		}
	};

	const startup = async () => {
		try {
			startedUp.current = true;
			opentok.current?.onStreamAdded(streamAdded);
			opentok.current?.onStreamRemoved(streamRemoved);
			opentok.current?.onReconnected(streamAdded);
			opentok.current?.onSessionDisconnect(handleReconnect);

			// if embedded, don't publish video
			if (
				mainVideoStream
				&& !embed
				&& !watcherOnly
				&& !expectIosStream
				&& user
			) {
				const { videoMuted, audioMuted } = getLocalMuteState();
				await opentok.current?.startStream(
					selectedVideo?.deviceId,
					selectedAudio?.deviceId,
					videoMuted,
					audioMuted,
					window.location.search.includes('iosEmulate') ? `mob-${user.gr_user}` : String(user.gr_user),
				);
				const webRTCId = String(opentok.current?.publisher?.streamId);

				updateWebRTCId(webRTCId);
				dispatch(addFeedWebRTCId(webRTCId));

				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.gr_user === user?.gr_user);

				if (connectedUser && webRTCId) {
					connectedUser.webrtcID = webRTCId;
					dispatch(userUpdated(connectedUser));
				}

				if (videoMuted) {
					opentok.current?.toggleVideo(!videoMuted);
				}

				if (audioMuted) {
					opentok.current?.toggleAudio(!audioMuted);
				}

				if (RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState)) {
					window.location.reload();
				}
			} else {
				await opentok.current?.subscribeOnly();
			}
		} catch (e: any) {
			if (token) {
				ReportError(
					token,
					navigator.userAgent,
					e.stack,
					e.message,
				).catch((e2) => console.error(e2));
			}

			if (publishAttempts.current < 10) {
				setTimeout(() => {
					opentok.current?.stop();
					opentok.current = null;
					dispatch(setConnectedPresenterVideos([]));
					publishAttempts.current += 1;
					videoFeedError();
					if (restrictedUserState !== RESTRICTED_USER_STATES.RECONNECTING) {
						dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.RECONNECTING));
					}
				}, 3000);
			} else {
				if (restrictedUserState !== RESTRICTED_USER_STATES.BLOCKED) {
					dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.BLOCKED));
				}
				showAlert({
					message: 'Video Error',
					description: 'We ran into an issue publishing your video to the room. Please ensure your camera is connected, no other programs are using it, and you have sufficient bandwidth.',
					type: 'neutral',
					duration: 5000,
				});
			}
		} finally {
			setOpentokInitialized(true);
		}
	};

	const initialSetup = async () => {
		resetOT();

		if (mainVideoStream && (!watcherOnly && !expectIosStream)) {
			const inPresenterVids = connectedPresenterVideos.find((p) => p.srcObject === mainVideoStream);
			if (!RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState) && !inPresenterVids) {
				const { videoMuted, audioMuted } = getLocalMuteState();
				dispatch(addPresenterVideo({
					id: 'local',
					srcObject: mainVideoStream,
					videoMuted,
					muted: audioMuted,
					mutedPreference: audioMuted,
					userInitiated: audioMuted,
					self: true,
					local: true,
					profile: user?.gr_user ?? 1,
					brandliveProfileId: user?.brandlive_profile,
				}));
			}

			try {
				await initTokbox();
				startup();
			} catch (e: any) {
				if (token) {
					ReportError(
						token,
						navigator.userAgent,
						e.stack,
						e.message,
					).catch((e2) => console.error(e2));
				}

				if (publishAttempts.current < 10) {
					setTimeout(() => {
						opentok.current?.stop();
						opentok.current = null;
						dispatch(setConnectedPresenterVideos([]));
						publishAttempts.current += 1;
						videoFeedError();

						if (restrictedUserState !== RESTRICTED_USER_STATES.RECONNECTING) {
							dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.RECONNECTING));
						}
					}, 3000);
				} else {
					if (restrictedUserState !== RESTRICTED_USER_STATES.BLOCKED) {
						dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.BLOCKED));
					}
					showAlert({
						message: 'Video Error',
						description: 'We ran into an issue connecting to the room. Please ensure no other programs are using your camera, and you have sufficient bandwidth.',
						type: 'neutral',
						duration: 5000,
					});
				}
			}
		} else if (!mainVideoStream || embed || watcherOnly || expectIosStream) {
			await initTokbox();
			startup();
		}
	};

	const restartPublisher = async () => {
		const { videoMuted, audioMuted } = getLocalMuteState();

		const newPublisher = await opentok.current?.restartStream(
			feedQuality,
			selectedVideo?.deviceId,
			selectedAudio?.deviceId,
			videoMuted,
			audioMuted,
		);

		if (!newPublisher?.stream) {
			console.log('no stream for publishing attempt');
			return;
		}

		if (newPublisher?.streamId === opentokId.current) {
			console.log('this stream has already been published');
			return;
		}

		const existingPublisherStreams = opentok.current?.myPublishers
			.map((p) => p.stream)
			.filter((s) => !!s && s.videoType !== 'screen' && s.streamId !== newPublisher?.streamId);

		existingPublisherStreams?.forEach((s) => {
			opentok.current?.stopPublishing(s?.streamId);
			console.log('old publisher exists, destroying it');
		});

		if (newPublisher?.streamId) {
			if (webrtcID) {
				dispatch(removeFeedWebRTCId(String(webrtcID)));
			}
			dispatch(addFeedWebRTCId(String(newPublisher?.streamId)));
			updateWebRTCId(newPublisher.streamId);
		}

		if (videoMuted) {
			opentok.current?.toggleVideo(!videoMuted);
		}

		if (audioMuted) {
			opentok.current?.toggleAudio(!audioMuted);
		}

		dispatch(addPresenterVideo({
			id: newPublisher?.streamId,
			srcObject: mainVideoStream,
			videoMuted,
			muted: audioMuted,
			mutedPreference: audioMuted,
			userInitiated: audioMuted,
			self: true,
			local: true,
			profile: user?.gr_user ?? 1,
			brandliveProfileId: user?.brandlive_profile,
		}));
	};

	useEffect(() => {
		if (!mainVideoStream && opentok.current?.publisher?.stream) {
			opentok.current.stopPublishing();
		}
	}, [mainVideoStream]);

	useEffect(() => {
		if (shouldRepublishVideo) {
			dispatch(VideoActions.setShouldRepublishVideo(false));

			if (
				!embed
				&& !watcherOnly
				&& !expectIosStream
				&& !!user
				&& opentokInitialized
			) {
				restartPublisher();
			}
		}
	}, [
		shouldRepublishVideo,
		opentokInitialized,
		embed,
		watcherOnly,
		expectIosStream,
		user,
	]);

	const prevSharingScreen = usePrevious(sharingScreen);
	const prevSelectedVideo = usePrevious(selectedVideo);
	const prevSelectedAudio = usePrevious(selectedAudio);
	const prevSelectedOutput = usePrevious(selectedOutput);
	const prevSocket = usePrevious(socket);

	const clientIsStreamingFromMobile = useMemo(() => {
		const clientConnectedVideo = connectedPresenterVideos.find(
			(video: VideoStream) => video.id === webrtcID,
		);
		return clientConnectedVideo?.isMobile ?? false;
	}, [connectedPresenterVideos, webrtcID]);

	useEffect(() => {
		if (prevSharingScreen !== sharingScreen) {
			if (sharingScreen) {
				startupOpentokScreenshare();
			} else {
				const destroyed = opentok.current?.stopScreensharing();

				if (destroyed) {
					let streams = connectedPresenterVideos;
					streams = streams.filter((stream: VideoStream) => stream.id !== String(destroyed));
					dispatch(setConnectedPresenterVideos(streams));
				}
			}
		}
	}, [sharingScreen, connectedPresenterVideos, prevSharingScreen]);

	const audioDebounce = useRef<NodeJS.Timeout>();
	const videoDebounce = useRef<NodeJS.Timeout>();

	useEffect(() => {
		if (prevSelectedVideo?.label !== selectedVideo?.label) { // using label for when default changes
			videoDebounce.current = setTimeout(() => {
				if (opentok.current && opentok.current?.publisher && selectedVideo?.deviceId) {
					opentok.current.changeVideoStream(selectedVideo.deviceId)
						.then((result) => {
							if (!result) {
								showAlert({
									message: 'Error Updating Video Device',
									description: `Cannot switch to video device ${selectedVideo.label}`,
									type: 'error',
								});
								if (prevSelectedVideo) {
									dispatch(VideoActions.setSelectedVideo(prevSelectedVideo));
								}
							} else {
								showAlert({
									message: 'Video Device Detected',
									description: `Connected to ${selectedVideo.label}`,
									type: 'cam-update',
								});
							}
						});
				}
			}, 500);
		}
	}, [selectedVideo, prevSelectedVideo, webrtcID]);

	useEffect(() => {
		if (prevSelectedAudio?.label !== selectedAudio?.label) { // using label for when default changes
			audioDebounce.current = setTimeout(() => {
				if (opentok.current && opentok.current?.publisher && selectedAudio?.deviceId) {
					opentok.current.changeAudioStream(selectedAudio.deviceId)
						.then((result) => {
							if (!result) {
								showAlert({
									message: 'Error Updating Microphone Device',
									description: `Cannot switch to device ${selectedAudio.label}`,
									type: 'error',
								});
								if (prevSelectedAudio) {
									dispatch(VideoActions.setSelectedAudio(prevSelectedAudio));
								}
							} else {
								showAlert({
									message: 'Audio Device Detected',
									description: `Connected to ${selectedAudio.label}`,
									type: 'mic-update',
								});
							}
						});
				}
			}, 500);
		}
	}, [selectedAudio, prevSelectedAudio, webrtcID]);

	useEffect(() => {
		// using label for when default changes
		if (prevSelectedOutput?.label !== selectedOutput?.label) {
			opentokElements.current.forEach((el: HTMLVideoElement) => {
				if (
					el
					&& el.srcObject
					&& selectedOutput
					&& selectedOutput.deviceId
					&& (el.srcObject as MediaStream).getAudioTracks()?.[0]
				) {
					(el as any).setSinkId(selectedOutput.deviceId).catch((e: Error) => {
						showAlert({
							message: 'Error Updating Speaker',
							description: `Cannot switch to device ${selectedOutput.label}`,
							type: 'error',
						});
						console.error('could not set audio output', e.toString());
					});
				}
			});

			// need prevSelectedOutput or alert will fire on load
			if (prevSelectedOutput && selectedOutput && selectedOutput.deviceId) {
				showAlert({
					message: 'Speaker Output Detected',
					description: `Connected to ${selectedOutput?.label}`,
					type: 'speaker-update',
				});
			}
		}
	}, [selectedOutput, prevSelectedOutput]);

	// giant componentDidUpdate from video-feed.tsx
	useEffect(() => {
		if (
			opentok.current?.session
			&& opentok.current.session.isConnected()
			&& restrictedUserState === RESTRICTED_USER_STATES.RECONNECTING
			&& (!embed || !watcherOnly)
			&& !opentok.current?.publisher
			&& mainVideoStream
		) {
			dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.BLOCKED));
		}

		/*
			If component initialized with socket and connection time changed,
			Run initial setup again to get new instance of OpenTok class.
			Socket connection time only changes when initially connecting and
			then when reconnecting.
		*/
		if (socket?.connectionTimestamp
			&& prevSocket?.connectionTimestamp
			&& socket?.connectionTimestamp !== prevSocket?.connectionTimestamp
		) {
			initialSetup();
		}

		if ((unmutedAudioFeeds?.length ?? 0) > 0) {
			unmutedAudioFeeds.forEach((streamId) => {
				if (opentok.current) {
					opentok.current.resubscribeToStreamIfVideoMuted(streamId);
				}
				dispatch({
					type: OPENTOK_STREAM_AUDIO_REMOVE_UNMUTED_ID,
					payload: streamId,
				});
			});
		}
	});

	useEffect(() => {
		/* if they have connected through the ios app, set streams to 0 volume in browser.
			Also disable Backstage Comms logic for client (using clientIsStreamingFromMobile) as
		this would turn the volume back on for these streams */
		if (clientIsStreamingFromMobile) {
			const numberOfStreams = opentokElements.current.length;
			for (let i = 0; i < numberOfStreams; i += 1) {
				opentokElements.current[i].volume = 0.0;
			}
		}

		// NOTE: Keep this onsite program feed logic ahead of the backstage comms logic below
		//
		// If this is an onsite program feed view we want to make the onsite users volume muted.
		// FYI - Onsite users can be turned on/off with right
		// click menu in thumbnail feeds by a producer so we need to listen to onsiteUsers
		// changes and adjust that user's volume on and off.
		// If an onsite user is dragged from feeds -> canvas need to adjust volume in shot
		connectedPresenterVideos.forEach((vid) => {
			// opentokElements are html <video> elements and their id's are stream ids from Opentok
			const vidElement = opentokElements.current.find((e) => e.id === vid.id);
			if (vidElement) {
				if (vid.profile && onsiteUsers && onsiteUsers.includes(vid.profile)) {
					// Onsite user
					vidElement.volume = 0.0;
				} else {
					vidElement.volume = 1.0;
				}
			}
		});

		// backstage comms
		/*
			this is an automatic stream mute that happens based off of who is live (in canvas)
			if a stream is live, we don't want them to hear anyone who isn't live
			so if a stream goes live, we tell that stream's matching application that every
			stream that isn't live should be muted, and every stream that is live should have audio out
			THIS IS DIFFERENT THAN A REGULAR MUTE
			a regular mute sets the person's audio output to 0 volume, and is reflected in their UI
			this mutes the actual video element, is automatic, and has no UI indication
		*/

		if (!embed && webrtcID && !clientIsStreamingFromMobile) {
			const liveIdsNoMedia = canvasWebRTCIds.filter((id) => !id.includes('media-share'));
			const numberOfStreams = opentokElements.current.length;

			if (
				liveIdsNoMedia.includes(webrtcID.toString())		// im in the shot
				&& backstageCommsActive					// bsc is toggled on for the room
				&& roomState === ROOM_STATES.SHOWTIME	// we're in the live state
				&& !clearShot							// the shot has not been hidden
			) {
				for (let i = 0; i < numberOfStreams; i += 1) {
					if (liveIdsNoMedia.includes(opentokElements.current[i].id)) {
						// if this stream is also live, unmute it
						opentokElements.current[i].volume = 1.0;
					} else {
						// im live but this stream is not, mute it
						opentokElements.current[i].volume = 0.0;
					}
				}
			} else {
				// state where backstage comms is not active
				// for me (even if it is toggled on for the room)
				for (let i = 0; i < numberOfStreams; i += 1) {
					opentokElements.current[i].volume = 1.0;
				}
			}
		}

		/* embed view backstage chat- we only want the embed
		view to hear streams that are in the canvas */
		/* if we're in pre or post show, we only want to hear the media for that stream state */
		if (embed) {
			const liveIdsNoMedia = canvasWebRTCIds.filter((id) => !id.includes('media-share'));
			const numberOfStreams = opentokElements.current.length;
			// if broadcast is in pre or post stream we want
			// to mute presenter streams for the embed view
			for (let i = 0; i < numberOfStreams; i += 1) {
				let shouldBeMuted = !liveIdsNoMedia.includes(opentokElements.current[i].id);

				if (!backstageCommsActive) { shouldBeMuted = false; }

				if (clearShot) {
					shouldBeMuted = true;
				}

				// Onsite program feed logic is above all of this backstage comms logic.
				// However, we don't want to accidently unmute an onsite user
				// that we muted above so we include this here.
				if (isOnsiteFeed && onsiteUsers && connectedPresenterVideos) {
					const userId = connectedPresenterVideos.find(
						(vid) => vid.id === opentokElements.current[i].id,
					)?.profile;
					if (userId && onsiteUsers.includes(userId)) { shouldBeMuted = true; }
				}

				opentokElements.current[i].volume = shouldBeMuted ? 0.0 : 1.0;
			}
		}

		// all other volume determination and opentokElements manipulation is done
		// apply canvas volume
		opentokElements.current.forEach((opentokElement) => {
			if (backstageCanvasVolumeOn && canvasWebRTCIds.includes(opentokElement.id)) {
				opentokElement.volume = backstageCanvasVolume;
			}
		});
	}, [
		shotFeeds,
		connectedPresenterVideos,
		backstageCommsActive,
		roomState,
		clearShot,
		onsiteUsers,
		clientIsStreamingFromMobile,
		isOnsiteFeed,
		canvasWebRTCIds,
		embed,
		backstageCanvasVolume,
		backstageCanvasVolumeOn,
	]);

	useEffect(() => {
		if (embed || isProgramFeed || isOnsiteFeed || !webrtcID) return;
		socket?.removeListener('backstage-comms-toggle');
		socket?.addListener(
			'backstage-comms-toggle',
			(message: any) => {
				applyBackstageComms(
					message,
					canvasWebRTCIds,
					clearShot,
					webrtcID.toString(),
				);
			},
		);
	}, [clearShot, webrtcID, canvasWebRTCIds, embed, socket, isProgramFeed, isOnsiteFeed]);

	useEffect(() => {
		if (embed || isProgramFeed || isOnsiteFeed || !webrtcID) return;
		if (canvasWebRTCIds.includes(webrtcID.toString())) {
			openCommValues?.forEach((value) => {
				const producer = connectedUsers.find((connUser) => connUser.gr_user === value.producer);
				if (!producer || !producer.webrtcID) return;
				applyBackstageComms(
					{
						type: 'backstage-comms-toggle',
						message: {
							muted: false,
							producerID: producer.webrtcID.toString(),
							targetedPresenterID: value.presenter ?? '',
						},
					} as BackstageCommsSocketMessage,
					canvasWebRTCIds,
					clearShot,
					webrtcID.toString(),
				);
			});
		}
	}, [clearShot, canvasWebRTCIds, webrtcID, openCommValues, embed, isProgramFeed, isOnsiteFeed]);

	const streams = useMemo(() => {
		try {
			const streamObjects = [];

			for (let i = 0; i < connectedPresenterVideos.length; i += 1) {
				if (connectedPresenterVideos[i].isScreenShare) {
					const connectedStreamUser = connectedUsers
						.find((connectedUser) => (
							connectedUser.screenShareWebrtcId === connectedPresenterVideos[i].id
						));

					if (connectedStreamUser) {
						streamObjects.push({
							gr_user: connectedStreamUser.gr_user,
							stream_id: connectedPresenterVideos[i].id,
							device_type: CONNECTED_PRESENTER_VIDEO_TYPES.SCREENSHARE,
						});
					}
				} else if (connectedPresenterVideos[i].profile) {
					streamObjects.push({
						gr_user: connectedPresenterVideos[i].profile,
						stream_id: connectedPresenterVideos[i].id,
						device_type: CONNECTED_PRESENTER_VIDEO_TYPES.CAMERA,
					});
				}
			}

			return streamObjects;
		} catch {
			return [];
		}
	}, [connectedPresenterVideos, connectedUsers]);

	useEffect(() => {
		if (isoArchiveId
			&& streams.length
			&& opentok.current
			&& opentok.current.session.sessionId
			&& meeting?.meeting
			&& embed
			&& token
		) {
			for (let i = 0; i < streams.length; i += 1) {
				SetStreamInfo(
					token,
					streams[i].stream_id,
					isoArchiveId,
					opentok.current!.session.sessionId,
					streams[i].gr_user!,
					meeting.meeting,
					streams[i].device_type,
				);
			}
		}
	}, [
		isoArchiveId,
		streams,
		opentok.current?.session?.sessionId,
		meeting?.meeting,
		embed,
	]);

	return {
		opentok: opentok.current,
		forceFeedsTabRefresh,
		setOT,
		initialSetup,
		restartPublisher,
	};
}

export const OpenTokProvider = (
	props: any,
): React.ReactElement<any, React.Provider<OpenTokContextInterface>> => {
	const opentokContextValue = useOpentok(props.socket);
	return <OpenTokContext.Provider value={opentokContextValue}>
		{props.children}
	</OpenTokContext.Provider>;
};
